import React, { useState } from 'react';
import './Education.css';
import universityImage from './uwaterloo-logo.png';
import highSchoolImage from './aba-logo.png';

const Education = () => {
  const [activeCategory, setActiveCategory] = useState('university');

  const educationData = {
    university: {
      institution: 'University of Waterloo',
      degree: 'Bachelor of Computer Science (BCS)',
      program: 'Co-operative Program',
      duration: 'Sep 2023 – Expected May 2028',
      location: 'Waterloo, Ontario',
      imageUrl: universityImage,
      stats: [
        { label: 'Cumulative GPA', value: '3.95 / 4.0' },
        { label: 'Overall Average', value: '91%' },
        { label: 'Status', value: "Candidate Dean's List" }
      ],
      courseworkCategories: [
        {
          title: 'Systems & Compilers',
          courses: ['Compilers (CS 241)', 'Operating Systems (CS 350)', 'Computer Organization & Design (CS 251)', 'Tools & Tech for Software Dev (CS 136L)']
        },
        {
          title: 'Algorithms & Computing',
          courses: ['Algorithms (CS 341)', 'Data Structures (CS 240E - Enriched)', 'Numerical Computation (CS 370)', 'Computational Vision (CS 484)', 'Object-Oriented Soft Dev (CS 246)', 'Logic & Computation (CS 245)', 'Functional Programming (CS 135)']
        },
        {
          title: 'Pure & Applied Mathematics',
          courses: ['Linear Algebra I & II (Honours)', 'Calculus I, II, III (Honours)', 'Intro Combinatorics & Graph Theory (MATH 239)', 'Probability & Statistics (STAT 230/231)', 'Algebra (MATH 135)']
        }
      ],
      honors: [
        "Candidate Dean's Honors List (Multiple Terms)",
        'President\'s Scholarship of Distinction',
        'Top ~20 in Small c Math Contest (for 1st & 2nd year students)',
        'Euclid Distinction Award',
        'Competitive Transfer from Mathematics to Computer Science'
      ],
      activities: [
        'UW Data Science Club',
        'Competitive Badminton Club',
        'Undergraduate Research in Compilers & TEE Security'
      ]
    },
    highschool: {
      institution: 'ABA Oman International School',
      degree: 'International Baccalaureate (IB) Diploma',
      program: 'IB Diploma Programme',
      duration: 'Graduated May 2023',
      location: 'Muscat, Oman',
      imageUrl: highSchoolImage,
      stats: [
        { label: 'IB DP Score', value: '44 / 45' },
        { label: 'Rank', value: 'Valedictorian' },
        { label: 'Recognition', value: 'School Topper' }
      ],
      courseworkCategories: [
        {
          title: 'Higher Level (HL)',
          courses: ['HL Mathematics: Analysis & Approaches - 7', 'HL Physics - 7', 'HL Chemistry - 7']
        },
        {
          title: 'Standard Level (SL)',
          courses: ['SL Business Management - 7', 'SL English A Language & Literature - 7', 'SL French - 6']
        }
      ],
      honors: [
        'Valedictorian & School Topper (Class of 2023)',
        'MESAC Academic Games: 1st Engineering Olympiad (3x) & 1st Overall',
        'Member of National Honor Society (NHS)'
      ],
      activities: [
        'High School Student Council: Secretary',
        'Coding Club Lead',
        'VFX Lead in EcoSoc (Sustainable Development)',
        'Varsity Volleyball & Tennis Representative'
      ]
    }
  };

  const current = educationData[activeCategory];

  return (
    <div className="education-page">
      <div className="education-container">
        {/* Navigation Switcher */}
        <div className="edu-nav-header">
          <div className="edu-tabs">
            <button
              className={`edu-tab-btn ${activeCategory === 'university' ? 'active' : ''}`}
              onClick={() => setActiveCategory('university')}
            >
              🎓 University of Waterloo
            </button>
            <button
              className={`edu-tab-btn ${activeCategory === 'highschool' ? 'active' : ''}`}
              onClick={() => setActiveCategory('highschool')}
            >
              🏫 High School (IB DP)
            </button>
          </div>
        </div>

        {/* Main Content Card */}
        <div className="edu-main-card">
          {/* Header section with branding */}
          <div className="edu-card-top">
            <img
              src={current.imageUrl}
              alt={current.institution}
              className="edu-institution-logo"
            />
            <div className="edu-header-text">
              <h2 className="edu-institution-name">{current.institution}</h2>
              <div className="edu-degree-line">
                <span className="edu-degree">{current.degree}</span>
                <span className="edu-separator">•</span>
                <span className="edu-program">{current.program}</span>
              </div>
              <div className="edu-meta-line">
                <span className="edu-duration">{current.duration}</span>
                <span className="edu-separator">•</span>
                <span className="edu-location">{current.location}</span>
              </div>
            </div>

            {activeCategory === 'university' && (
              <a
                href="https://drive.google.com/file/d/1ctNPo9hbtKY2yxmDgyPl9JkysuVaTbli/view?usp=drive_link"
                target="_blank"
                rel="noopener noreferrer"
                className="edu-action-cta-btn"
              >
                📄 View Transcript
              </a>
            )}
          </div>

          {/* Key Stat Cards */}
          <div className="edu-stats-grid">
            {current.stats.map((stat, idx) => (
              <div className="edu-stat-box" key={idx}>
                <span className="edu-stat-label">{stat.label}</span>
                <span className="edu-stat-val">{stat.value}</span>
              </div>
            ))}
          </div>

          {/* Coursework Section */}
          <div className="edu-section">
            <h3 className="edu-section-title">Key Coursework</h3>
            <div className="edu-course-categories">
              {current.courseworkCategories.map((cat, cIdx) => (
                <div className="edu-course-category-card" key={cIdx}>
                  <h4 className="edu-category-label">{cat.title}</h4>
                  <div className="edu-course-pills">
                    {cat.courses.map((course, idx) => (
                      <span className="edu-course-pill" key={idx}>
                        {course}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Honors & Activities Two-Column Grid */}
          <div className="edu-two-column-grid">
            <div className="edu-sub-card">
              <h3 className="edu-section-title">🏆 Honors & Awards</h3>
              <ul className="edu-list">
                {current.honors.map((honor, hIdx) => (
                  <li key={hIdx}>{honor}</li>
                ))}
              </ul>
            </div>

            <div className="edu-sub-card">
              <h3 className="edu-section-title">⚡ Activities & Involvements</h3>
              <ul className="edu-list">
                {current.activities.map((act, aIdx) => (
                  <li key={aIdx}>{act}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Education;
